import { type ReactNode, useId, useState } from 'react';

function ShortQuote() {
  return (
    <blockquote className="newsletter-testimonial-quote newsletter-testimonial-quote-short">
      <p>
        <span className="newsletter-testimonial-opening-quote">“</span>Thanks to Atrivial, I can
        publish news quizzes with{' '}
        <span className="newsletter-testimonial-highlight">a lot more flexibility</span>.”
      </p>
    </blockquote>
  );
}

function FullQuote() {
  return (
    <blockquote className="newsletter-testimonial-quote newsletter-testimonial-quote-full">
      <p>
        <span className="newsletter-testimonial-opening-quote">“</span>Even back during my days as
        editor-in-chief of the University of South Florida Oracle newspaper, I liked to publish news
        quizzes for readers. Thanks to Atrivial, I’ve got the capacity to do that for Southpoint
        Access News readers&nbsp;- but with{' '}
        <span className="newsletter-testimonial-highlight">a lot more flexibility</span> than the
        old hot wax and paper layout days.
      </p>
      <p>
        It’s great for encouraging my readers to test their recall of&nbsp;- or share opinions
        about&nbsp;- articles appearing in my newsletters. And they’ve consistently iterated new
        features in just the past few months I’ve been working with them.
      </p>
      <p>
        So&nbsp;- if you run a digital news site or publish a newsletter on just about any topic,
        and you want a trivia feature to help build engagement and connection, check them out!”
      </p>
    </blockquote>
  );
}

interface TestimonialCardProps {
  name: string;
  role: string;
  portrait?: string;
  portraitSrcSet?: string;
  portraitSizes?: string;
  portraitClassName?: string;
  publication: string;
  logo: string;
  logoSrcSet?: string;
  logoSizes?: string;
  logoWidth: number;
  logoHeight: number;
  shortQuote: ReactNode;
  fullQuote?: ReactNode;
}

function TestimonialCard({
  name,
  role,
  portrait,
  portraitSrcSet,
  portraitSizes,
  portraitClassName,
  publication,
  logo,
  logoSrcSet,
  logoSizes,
  logoWidth,
  logoHeight,
  shortQuote,
  fullQuote,
}: TestimonialCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const fullQuoteId = useId();

  return (
    <figure className="newsletter-testimonial-card" data-expanded={isExpanded} aria-label={name}>
      {fullQuote && (
        <button
          type="button"
          className="newsletter-testimonial-card-toggle"
          aria-label={isExpanded ? 'Show excerpt' : 'Read full quote'}
          aria-controls={fullQuoteId}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        />
      )}

      {fullQuote ? (
        <div className="newsletter-testimonial-stage">
          <div
            className="newsletter-testimonial-sizer newsletter-testimonial-sizer-short"
            aria-hidden="true"
          >
            <div>{shortQuote}</div>
          </div>
          <div
            className="newsletter-testimonial-sizer newsletter-testimonial-sizer-full"
            aria-hidden="true"
          >
            <div>{fullQuote}</div>
          </div>

          <div
            className="newsletter-testimonial-layer newsletter-testimonial-layer-short"
            aria-hidden={isExpanded}
          >
            {shortQuote}
          </div>
          <div
            id={fullQuoteId}
            className="newsletter-testimonial-layer newsletter-testimonial-layer-full"
            aria-hidden={!isExpanded}
          >
            {fullQuote}
          </div>
        </div>
      ) : (
        <div className="newsletter-testimonial-stage">{shortQuote}</div>
      )}
      {fullQuote && (
        <span className="newsletter-testimonial-toggle-label" aria-hidden="true">
          {isExpanded ? 'Show excerpt' : 'Read full quote'}
        </span>
      )}

      <figcaption className="newsletter-testimonial-byline" data-has-portrait={Boolean(portrait)}>
        {portrait && (
          <span className="newsletter-testimonial-portrait">
            <img
              className={portraitClassName}
              src={portrait}
              srcSet={portraitSrcSet}
              sizes={portraitSizes}
              alt={name}
              width={240}
              height={240}
              loading="lazy"
              decoding="async"
            />
          </span>
        )}
        <div className="newsletter-testimonial-credit">
          <p className="newsletter-testimonial-name">{name}</p>
          <p className="newsletter-testimonial-role">{role}</p>
        </div>
        <img
          className="newsletter-testimonial-publication"
          src={logo}
          srcSet={logoSrcSet}
          sizes={logoSizes}
          alt={publication}
          width={logoWidth}
          height={logoHeight}
          loading="lazy"
          decoding="async"
        />
      </figcaption>
    </figure>
  );
}

export default function Testimonial() {
  return (
    <section
      data-newsletter-section="testimonial"
      className="newsletter-testimonial-row"
      aria-label="Customer testimonials"
      tabIndex={0}
    >
      <TestimonialCard
        name="Des Brown"
        portrait="/for-newsletters/des-brown-318w.webp"
        portraitSrcSet="/for-newsletters/des-brown-106w.webp 106w, /for-newsletters/des-brown-212w.webp 212w, /for-newsletters/des-brown-318w.webp 318w"
        portraitSizes="106px"
        portraitClassName="newsletter-testimonial-portrait-des"
        role="Email Advice in Your Inbox"
        publication="Email Advice in Your Inbox"
        logo="/for-newsletters/email-advice-in-your-inbox.svg"
        logoWidth={3665}
        logoHeight={2079}
        shortQuote={
          <blockquote className="newsletter-testimonial-quote newsletter-testimonial-quote-short">
            <p>
              <span className="newsletter-testimonial-opening-quote">“</span>The play-then-return
              loop that scoring and streaks create is a legitimate re-engagement lever, not just a
              gimmick.”
            </p>
          </blockquote>
        }
      />
      <TestimonialCard
        name="Wes Platt"
        role="Editor and Publisher, Southpoint Access"
        portrait="/for-newsletters/wes-platt-144w.webp"
        portraitSrcSet="/for-newsletters/wes-platt-48w.webp 48w, /for-newsletters/wes-platt-96w.webp 96w, /for-newsletters/wes-platt-144w.webp 144w"
        portraitSizes="48px"
        publication="Southpoint Access"
        logo="/for-newsletters/southpoint-access.svg"
        logoWidth={126}
        logoHeight={160}
        shortQuote={<ShortQuote />}
        fullQuote={<FullQuote />}
      />
      <TestimonialCard
        name="Aurelie Banoun"
        role="CEO, LetterTrader"
        portrait="/for-newsletters/aurelie-banoun-144w.webp"
        portraitSrcSet="/for-newsletters/aurelie-banoun-48w.webp 48w, /for-newsletters/aurelie-banoun-96w.webp 96w, /for-newsletters/aurelie-banoun-144w.webp 144w"
        portraitSizes="48px"
        publication="LetterTrader"
        logo="/for-newsletters/lettertrader.webp"
        logoSrcSet="/for-newsletters/lettertrader-76w.webp 76w, /for-newsletters/lettertrader-152w.webp 152w, /for-newsletters/lettertrader-228w.webp 228w, /for-newsletters/lettertrader.webp 657w"
        logoSizes="76px"
        logoWidth={657}
        logoHeight={96}
        shortQuote={
          <blockquote className="newsletter-testimonial-quote newsletter-testimonial-quote-short">
            <p>
              <span className="newsletter-testimonial-opening-quote">“</span>Giving operators a way
              to connect directly with their audience and, just as importantly,{' '}
              <span className="newsletter-testimonial-highlight">hear back from them</span> is
              incredibly valuable.”
            </p>
          </blockquote>
        }
        fullQuote={
          <blockquote className="newsletter-testimonial-quote newsletter-testimonial-quote-full">
            <p>
              <span className="newsletter-testimonial-opening-quote">“</span>What you’ve built with
              Atrivial is really exciting. Giving operators a way to connect directly with their
              audience and, just as importantly,{' '}
              <span className="newsletter-testimonial-highlight">hear back from them</span> is
              incredibly valuable. In my view, that direct audience relationship is becoming one of
              the most important parts of a newsletter’s value today.”
            </p>
          </blockquote>
        }
      />
    </section>
  );
}
